import './assetCarousel.css'
import { connect, type ConnectedProps } from 'react-redux'
import { type RootState } from '../../../../store'
import { useEffect, useState } from 'react'
import { fetchAssetImageByName } from '../../redux/actionCreator'
import { type AssetPreviewDataState, type AssetPreviewObject } from '../../redux/slices/assetPreviewImagesSlice'
import { CircularProgress } from '@mui/material'
import ImageError from '../../../../assets/ImageError.svg'
import LanguageStrings from '../../../../i18n/locales'
import CloseIcon from '@mui/icons-material/Close'

const ComponentStrings = LanguageStrings().Components

function getNextSymblol (enabled: boolean): JSX.Element {
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg" opacity={enabled ? '1' : '0.5'}>
      <path d="M43.9999 80.6667C64.2504 80.6667 80.6666 64.2505 80.6666 44C80.6666 23.7496 64.2504 7.33337 43.9999 7.33337C23.7495 7.33337 7.33325 23.7496 7.33325 44C7.33325 64.2505 23.7495 80.6667 43.9999 80.6667Z" stroke="#F0F0F0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M44 58.6667L58.6667 44L44 29.3334" stroke="#F0F0F0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M29.3333 44H58.6666" stroke="#F0F0F0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function getPrevSymblol (enabled: boolean): JSX.Element {
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg" opacity={enabled ? '1' : '0.5'}>
      <path d="M43.9999 80.6667C64.2504 80.6667 80.6666 64.2505 80.6666 44C80.6666 23.7496 64.2504 7.33337 43.9999 7.33337C23.7495 7.33337 7.33325 23.7496 7.33325 44C7.33325 64.2505 23.7495 80.6667 43.9999 80.6667Z" stroke="#F0F0F0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M43.9999 29.3334L29.3333 44L43.9999 58.6667" stroke="#F0F0F0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M58.6666 44H29.3333" stroke="#F0F0F0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function AssetCarousel (props: AllProps): JSX.Element {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
  const [currentImage, setCurrentImage] = useState<AssetPreviewObject>(props.assetPreviewImages.assetPreview[props.assetId].assetPreview[0])

  useEffect(() => {
    const body = document.querySelector('body')
    if (body != null) {
      body.style.overflow = 'hidden'
    }
    return () => {
      if (body != null) {
        body.style.overflow = 'auto'
      }
    }
  }, [])

  useEffect(() => {
    const assetImageAtIndex = props.assetPreviewImages.assetPreview[props.assetId]?.assetPreview[currentImageIndex]
    setCurrentImage(assetImageAtIndex)
    console.log('Current Image:', assetImageAtIndex.image)
  }, [props.assetPreviewImages])

  function handleImageChange (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, direction: 'prev' | 'next'): void {
    e.preventDefault()
    if (direction === 'prev' && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1)
      if (props.assetPreviewImages.assetPreview[props.assetId].assetPreview[currentImageIndex - 1].image.length === 0) {
        props.fetchAssetImageByName(props.assetId, props.assetPreviewImages.assetPreview[props.assetId].assetPreview[currentImageIndex - 1].name)
      } else {
        setCurrentImage(props.assetPreviewImages.assetPreview[props.assetId].assetPreview[currentImageIndex - 1])
      }
    } else if (direction === 'next' && currentImageIndex < props.assetPreviewImages.assetPreview[props.assetId].assetPreview.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1)
      if (props.assetPreviewImages.assetPreview[props.assetId].assetPreview[currentImageIndex + 1].image.length === 0) {
        props.fetchAssetImageByName(props.assetId, props.assetPreviewImages.assetPreview[props.assetId].assetPreview[currentImageIndex + 1].name)
      } else {
        setCurrentImage(props.assetPreviewImages.assetPreview[props.assetId].assetPreview[currentImageIndex + 1])
      }
    }
  }

  function closeWindow (e: React.MouseEvent): void {
    if (e.target === e.currentTarget) {
      props.handleCloseCarousel()
    }
  }

  return (
    <div className='asset-carousel-window' onClick={closeWindow} data-testid='asset-carousel-window'>
      <div className='asset-carousel'>
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            cursor: 'pointer',
            color: '#F0F0F0'
          }}
          onClick={props.handleCloseCarousel}
        >
          <CloseIcon/>
        </div>
        <div style={{ height: '5vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontFamily: 'Honeywell Sans Web', fontSize: '20px', fontStyle: 'italic' }}>{currentImage.name}</p>
        </div>
        <div className='carousel-image-section'>
          <span data-testid='carousel-prev-button' style={{ width: '10%', cursor: 'pointer' }} onClick={(e) => { handleImageChange(e, 'prev') }}>{getPrevSymblol(currentImageIndex > 0)}</span>
          <div style={{ width: '80%', padding: 10, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className='carousel-image-container'>
            {
              currentImage.isLoading
                ? <CircularProgress data-testid='asset-image-circular-progress' size={'2rem'} />
                : (currentImage.error)
                    ? <div className='carousel-image-error' data-testid='asset-image-error'>
                      <img src={ImageError} alt={ComponentStrings.ErrorLoadingImage} style={{ maxWidth: '30vw', maxHeight: '30vh' }} />
                      <p>{ComponentStrings.UnableToLoad}</p>
                  </div>
                    : <img
                      src={currentImage.image}
                      alt={`Asset Preview ${currentImageIndex + 1}`}
                      data-testid='asset-image'
                    />
            }
          </div>
          <span data-testid='carousel-next-button' style={{ width: '10%', cursor: 'pointer' }} onClick={(e) => { handleImageChange(e, 'next') }}>{getNextSymblol(currentImageIndex < props.assetPreviewImages.assetPreview[props.assetId].assetPreview.length - 1)}</span>
        </div>
        <div className='carousel-thumbnails-list'>
          {
            props.assetPreviewImages.assetPreview[props.assetId].assetPreview.map((assetPrevObj, index) => (
              <div
                key={index}
                className={`carousel-thumbnail ${currentImageIndex === index ? 'active' : ''}`}
                onClick={() => {
                  setCurrentImageIndex(index)
                  if (props.assetPreviewImages.assetPreview[props.assetId].assetPreview[index].image.length === 0) {
                    props.fetchAssetImageByName(props.assetId, assetPrevObj.name)
                  } else {
                    setCurrentImage(props.assetPreviewImages.assetPreview[props.assetId].assetPreview[index])
                  }
                }}
              >
                <img style={{ border: index === currentImageIndex ? '4px solid #68C8FE' : 'none', borderRadius: '10px', width: 'calc(18vh - 80px)', height: 'calc(18vh - 80px)', minWidth: 60, minHeight: 60 }} src={assetPrevObj.thumbnail} alt={`Thumbnail ${index + 1}`} />
              </div>
            ))
          }
        </div>
      </div>
    </div>
  )
}

interface DispatchToProps {
  fetchAssetImageByName: (assetId: string, name: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchAssetImageByName: (assetId: string, name: string) => dispatch(fetchAssetImageByName(assetId, name))
})

interface StateToProps {
  assetPreviewImages: AssetPreviewDataState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  assetPreviewImages: state.assetPreviewImages
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

interface ParentProps {
  handleCloseCarousel: () => void
  assetId: string
}

type AllProps = PropsFromRedux & ParentProps

export default connector(AssetCarousel)
